import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Users,
  BookOpen,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  BarChart3,
  Loader2,
  User,
  Calendar
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeacherPerformancePage = () => {
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('all_time');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDetail, setTeacherDetail] = useState(null);

  useEffect(() => {
    fetchPerformance();
  }, [period]);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/analytics/teachers?period=${period}`);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherDetail = async (teacherId) => {
    try {
      const res = await axios.get(`${API_URL}/analytics/teachers/${teacherId}`);
      setTeacherDetail(res.data);
      setSelectedTeacher(teacherId);
    } catch (error) {
      console.error('Error fetching teacher detail:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teacher-performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            Performance des enseignants
          </h1>
          <p className="text-gray-500">Analysez les métriques de performance de votre équipe pédagogique</p>
        </div>
        
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_time">Tout le temps</SelectItem>
            <SelectItem value="this_month">{lang === 'de' ? 'Diesen Monat' : 'Ce mois'}</SelectItem>
            <SelectItem value="this_year">Cette année</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-primary to-primary/80 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 opacity-80" />
              <span className="text-4xl font-bold">{data?.total_teachers || 0}</span>
            </div>
            <p className="text-white/80">Enseignants actifs</p>
          </CardContent>
        </Card>

        {data?.best_attendance_rate && (
          <Card className="border-0 shadow-sm bg-gradient-to-br from-secondary to-secondary/80 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5" />
                <span className="text-sm opacity-80">Meilleur taux de présence</span>
              </div>
              <p className="text-2xl font-bold">{data.best_attendance_rate.name}</p>
              <p className="text-white/80">{data.best_attendance_rate.rate}%</p>
            </CardContent>
          </Card>
        )}

        {data?.best_completion_rate && (
          <Card className="border-0 shadow-sm bg-gradient-to-br from-accent to-accent/80 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5" />
                <span className="text-sm opacity-80">Meilleur taux de complétion</span>
              </div>
              <p className="text-2xl font-bold">{data.best_completion_rate.name}</p>
              <p className="text-white/80">{data.best_completion_rate.rate}%</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Teachers Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading">Détail par enseignant</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.teachers?.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun enseignant trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">{lang === 'de' ? 'Lehrkraft' : 'Enseignant'}</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">{t('courses_title', lang)}</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">{t('students_title', lang)}</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">{t('course_detail_sessions', lang)}</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">Présence</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">Complétion</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">Score Quiz</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">{t('elearning_lessons', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.teachers?.map((teacher) => (
                    <tr 
                      key={teacher.teacher_id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => fetchTeacherDetail(teacher.teacher_id)}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-medium">{teacher.teacher_name}</span>
                        </div>
                      </td>
                      <td className="text-center py-4 px-4">
                        <Badge variant="outline">
                          {teacher.active_courses}/{teacher.total_courses}
                        </Badge>
                      </td>
                      <td className="text-center py-4 px-4">
                        <span className="font-medium">{teacher.total_students}</span>
                      </td>
                      <td className="text-center py-4 px-4">
                        <span className="text-gray-500">
                          {teacher.sessions_completed}/{teacher.total_sessions}
                        </span>
                      </td>
                      <td className="text-center py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={teacher.average_attendance_rate} className="w-16 h-2" />
                          <span className="text-sm">{teacher.average_attendance_rate}%</span>
                        </div>
                      </td>
                      <td className="text-center py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={teacher.completion_rate} className="w-16 h-2" />
                          <span className="text-sm">{teacher.completion_rate}%</span>
                        </div>
                      </td>
                      <td className="text-center py-4 px-4">
                        {teacher.average_quiz_score ? (
                          <Badge className={teacher.average_quiz_score >= 70 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {teacher.average_quiz_score}%
                          </Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-center py-4 px-4">
                        <span className="font-medium">{teacher.total_lessons_created}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Teacher Detail Panel */}
      {selectedTeacher && teacherDetail && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">
              Détails - {teacherDetail.teacher?.first_name} {teacherDetail.teacher?.last_name}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelectedTeacher(null)}>
              Fermer
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Courses */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Cours assignés
                </h4>
                <div className="space-y-2">
                  {teacherDetail.courses?.map(course => (
                    <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{course.name}</p>
                        <p className="text-sm text-gray-500">Niveau {course.level}</p>
                      </div>
                      <Badge className={course.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {course.status === 'active' ? 'Actif' : 'Inactif'}
                      </Badge>
                    </div>
                  ))}
                  {teacherDetail.courses?.length === 0 && (
                    <p className="text-gray-400 text-sm">Aucun cours assigné</p>
                  )}
                </div>
              </div>

              {/* Monthly Stats */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Sessions par mois (6 derniers mois)
                </h4>
                <div className="space-y-2">
                  {teacherDetail.monthly_stats?.map(stat => (
                    <div key={stat.month} className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-24">{stat.month}</span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${Math.min((stat.sessions / 30) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8">{stat.sessions}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeacherPerformancePage;
